import abbreviate from 'number-abbreviate';
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import './UserDashboardScreenInformation.css';
import { getUserDashboardScreenInfo } from '../../../../services/getUserDashboardScreenInfo';
import { getUserScreenRevenue } from '../../../../services/getUserScreenRevenue';
import { getUserScreenActivity } from '../../../../services/getUserScreenActivity';
import { updateUserContentSlot } from '../../../../services/updateUserContentSlot';
import ToggleButton from 'react-toggle-button'
import ReactjsAlert from 'reactjs-alert';
import { toggleScreenActive } from '../../../../services/toggleScreenOn';
import { updateScreenDisplayOrientation } from '../../../../services/updateScreenDisplayOrientation';
import { updateScreenName } from '../../../../services/updateScreenName';
import { updateScreenSchedule } from '../../../../services/updateScreenSchedule';
import Select from 'react-select';
import { getAdvertiserCategories } from '../../../../services/getAdvertisementCategories';
import { getBlacklistedAdCategories } from '../../../../services/getBlacklistedAdvertisementCategories ';
import { updateBlacklistedAdCategories } from '../../../../services/ updateBlacklistedAdCategories';
import RingLoader from "react-spinners/RingLoader";
import SkeletonText from '../../../../components/SkeletonText/SkeletonText';
import { getUserAccessToken, getUserSession, persistUserSession } from '../../../../services/userSession';
import { resolveScreenHostContext } from '../../../../services/resolveScreenHostContext';
import { hostRoleCanManageSettings, hostRoleCanViewRevenue } from '../../../../services/screenHostTeam';
import ContentTypeModal from '../../../../components/ContentPlugins/ContentTypeModal';
import LibraryPickerModal from '../../../../components/ContentLibrary/LibraryPickerModal';
import PluginPickerModal from '../../../../components/ContentPlugins/PluginPickerModal';
import WeatherPluginConfig from '../../../../components/ContentPlugins/WeatherPluginConfig';
import SportsPluginConfig from '../../../../components/ContentPlugins/SportsPluginConfig';
import SocialPluginConfig from '../../../../components/ContentPlugins/SocialPluginConfig';
import TriviaPluginConfig from '../../../../components/ContentPlugins/TriviaPluginConfig';
import PluginSlotBadge from '../../../../components/ContentPlugins/PluginSlotBadge';
import ContentReviewStatusModal from '../../../../components/ContentPlugins/ContentReviewStatusModal';
import {
  getPlugins,
  clearContentSlot,
} from '../../../../services/contentPlugins';
import { usePluginSlotJobs } from '../../../../hooks/usePluginSlotJobs';
import { prepareImageFileForUpload } from '../../../../utils/creativeAspectRatio';

const PERSONAL_IMAGE_MAX_BYTES = 3145728;
const PERSONAL_VIDEO_MAX_BYTES = 80 * 1024 * 1024;

function contentReviewSeenKey(screenId, slotId, reviewAt) {
  return `invert:contentReviewSeen:${screenId}:${slotId}:${reviewAt}`;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' && dataUrl.includes(',')
        ? dataUrl.split(',')[1]
        : dataUrl;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function isContentReviewSeen(screenId, slotId, reviewAt) {
  if (!reviewAt) return true;
  try {
    return localStorage.getItem(contentReviewSeenKey(screenId, slotId, reviewAt)) === '1';
  } catch {
    return false;
  }
}

function markContentReviewSeen(screenId, slotId, reviewAt) {
  if (!reviewAt) return;
  try {
    localStorage.setItem(contentReviewSeenKey(screenId, slotId, reviewAt), '1');
  } catch {
    // ignore storage errors
  }
}

const ISO_WEEKDAY_OPTIONS = [
  { label: 'Monday', value: 0 },
  { label: 'Tuesday', value: 1 },
  { label: 'Wednesday', value: 2 },
  { label: 'Thursday', value: 3 },
  { label: 'Friday', value: 4 },
  { label: 'Saturday', value: 5 },
  { label: 'Sunday', value: 6 },
];

function getTimezoneSelectOptions() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone').map((tz) => ({ label: tz, value: tz }));
    }
  } catch (e) {
    console.warn(e);
  }
  return [
    { label: 'UTC', value: 'UTC' },
    { label: 'America/New_York', value: 'America/New_York' },
    { label: 'America/Chicago', value: 'America/Chicago' },
    { label: 'America/Denver', value: 'America/Denver' },
    { label: 'America/Los_Angeles', value: 'America/Los_Angeles' },
    { label: 'Europe/London', value: 'Europe/London' },
  ];
}

const TIMEZONE_SELECT_OPTIONS = getTimezoneSelectOptions();

const DEFAULT_SCHEDULE_WINDOW = { dows: [0], start: '09:00', end: '17:00' };

function scheduleRulesToWindows(rules) {
  if (!Array.isArray(rules) || !rules.length) {
    return [{ ...DEFAULT_SCHEDULE_WINDOW }];
  }
  const groups = new Map();
  rules.forEach((r) => {
    const start = typeof r.start === 'string' ? r.start : '09:00';
    const end = typeof r.end === 'string' ? r.end : '17:00';
    const key = `${start}|${end}`;
    if (!groups.has(key)) {
      groups.set(key, { dows: [], start, end });
    }
    groups.get(key).dows.push(typeof r.dow === 'number' ? r.dow : 0);
  });
  return Array.from(groups.values()).map((g) => ({
    start: g.start,
    end: g.end,
    dows: [...new Set(g.dows)].sort((a, b) => a - b),
  }));
}

function scheduleWindowsToRules(windows) {
  const rules = [];
  windows.forEach((w) => {
    const dows = Array.isArray(w.dows) && w.dows.length ? w.dows : [0];
    dows.forEach((dow) => {
      rules.push({ dow, start: w.start, end: w.end });
    });
  });
  return rules;
}

function firstUnusedDow(windows) {
  const used = new Set(windows.flatMap((w) => w.dows || []));
  for (let d = 0; d <= 6; d += 1) {
    if (!used.has(d)) return d;
  }
  return 0;
}

function fileExtensionLower(name) {
  if (!name || typeof name !== 'string') return '';
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function isPersonalContentVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const base = url.split('?')[0].toLowerCase();
  return /\.(mp4|webm|mov|m4v|mkv)$/.test(base);
}
export default function UserDashboardScreenInformation() {
  const [screensActivity, setScreensActivity] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || (() => {
    try {
      const savedState = sessionStorage.getItem('userDashboardScreenInformationState');
      return savedState ? JSON.parse(savedState) : getUserSession();
    } catch (e) {
      console.error(e);
      return getUserSession();
    }
  })();
  const [ stats, setStats ] = useState({});
  const [hoursActive, setHoursActive] = useState(0);
  const [screenDataForGraph, setScreenDataForGraph] = useState({});
  const [contentToReplace, setContentToReplace] = useState();
  const [alertActive, setAlertActive] = useState(false);
  const [screenOn, setScreenOn] = useState(false);
  const [displayOrientation, setDisplayOrientation] = useState('landscape');
  const [contentRotation, setContentRotation] = useState('none');
  const [playbackActive, setPlaybackActive] = useState(false);
  const [scheduleEnabledUi, setScheduleEnabledUi] = useState(false);
  const [scheduleTimezone, setScheduleTimezone] = useState('');
  const [scheduleWindows, setScheduleWindows] = useState([{ ...DEFAULT_SCHEDULE_WINDOW }]);
  const [scheduleSaveError, setScheduleSaveError] = useState('');
  const [personalContentSlots, setPersonalContentSlots] = useState([]); // Personal content slots (1-4)
  const [advertisingSlots, setAdvertisingSlots] = useState([]); // Ad content slots (1-6) - from API
  const [allCategories, setAllCategories] = useState([]);
  const [selectedBlacklistCategories, setSelectedBlacklistCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coreLoading, setCoreLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [contentTypeModalOpen, setContentTypeModalOpen] = useState(false);
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [pluginPickerOpen, setPluginPickerOpen] = useState(false);
  const [weatherConfigOpen, setWeatherConfigOpen] = useState(false);
  const [sportsConfigOpen, setSportsConfigOpen] = useState(false);
  const [socialConfigOpen, setSocialConfigOpen] = useState(false);
  const [triviaConfigOpen, setTriviaConfigOpen] = useState(false);
  const [availablePlugins, setAvailablePlugins] = useState([]);
  const [pluginSaving, setPluginSaving] = useState(false);
  const [pluginRenderingSlot, setPluginRenderingSlot] = useState(null);
  const [contentReviewModal, setContentReviewModal] = useState(null);
  const [screenNameDraft, setScreenNameDraft] = useState('');
  const [screenNameError, setScreenNameError] = useState('');
  const [screenNameSaving, setScreenNameSaving] = useState(false);
  const pluginReviewFollowUpRef = useRef(null);
  const token = getUserAccessToken() || secureLocalStorage.getItem("tk") || state?.token;
  const { userId: ownerUserId, screenId: activeScreenId, role: hostRole } = resolveScreenHostContext(state, token);
  const canManageSettings = hostRoleCanManageSettings(hostRole);
  const canViewRevenue = hostRoleCanViewRevenue(hostRole);

  const {
    startPluginJob,
    isSlotRendering,
    getSlotPending,
    cancelPendingJobForSlot,
  } = usePluginSlotJobs({
    screenId: activeScreenId,
    token,
  });

  function appendCacheBuster(url, cacheKey = Date.now()) {
    if (!url) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${cacheKey}`;
  }
  
  function getAllCategories() {
    setCategoriesLoading(true);
    Promise.all([
      getAdvertiserCategories(token),
      canManageSettings && ownerUserId
        ? getBlacklistedAdCategories(token, ownerUserId)
        : Promise.resolve({ data: { data: [] } }),
    ])
      .then(([catResult, blacklistResult]) => {
        const options = (catResult.data.categories || []).map((category) => ({
          label: category.CategoryName,
          value: category.CategoryId,
        }));
        setAllCategories(options);
        const selectedCategories = [];
        (blacklistResult.data?.data || []).forEach((c) => {
          const category = options.find((cat) => cat.value === c.AdvertiserCategoryId);
          if (category) {
            selectedCategories.push(category);
          }
        });
        setSelectedBlacklistCategories(selectedCategories);
      })
      .catch((e) => {
        console.log(e);
        setAllCategories([]);
        setSelectedBlacklistCategories([]);
      })
      .finally(() => setCategoriesLoading(false));
  }
  function formatLastActive(lastActiveString) {
    console.log(lastActiveString)
    const date = new Date(lastActiveString);
    return date.toLocaleString();
}
  async function openSlotEditor(slotPosition) {
    const slot = personalContentSlots.find((s) => s.slotPosition === slotPosition);
    if (slot?.reviewStatus === 'pending_approval' && slot?.pendingContentUrl) {
      setContentReviewModal({ slotPosition, mode: 'pending' });
      return;
    }
    if (
      slot?.lastReviewStatus
      && slot?.lastReviewAt
      && !isContentReviewSeen(activeScreenId, slotPosition, slot.lastReviewAt)
    ) {
      setContentReviewModal({ slotPosition, mode: slot.lastReviewStatus });
      return;
    }

    setContentToReplace(slotPosition);

    if (isSlotRendering(slotPosition)) {
      const pending = getSlotPending(slotPosition);
      if (pending?.pluginId) {
        setPluginRenderingSlot(slotPosition);
        if (pending.pluginId === 'weather') {
          setWeatherConfigOpen(true);
          return;
        }
        if (pending.pluginId === 'sports') {
          setSportsConfigOpen(true);
          return;
        }
        if (pending.pluginId === 'social') {
          setSocialConfigOpen(true);
          return;
        }
        if (pending.pluginId === 'trivia') {
          setTriviaConfigOpen(true);
          return;
        }
      }
    }

    if (slot?.contentType === 'plugin' && slot.pluginId) {
      if (slot.pluginId === 'weather') {
        setWeatherConfigOpen(true);
        return;
      }
      if (slot.pluginId === 'sports') {
        setSportsConfigOpen(true);
        return;
      }
      if (slot.pluginId === 'social') {
        setSocialConfigOpen(true);
        return;
      }
      if (slot.pluginId === 'trivia') {
        setTriviaConfigOpen(true);
        return;
      }
    }
    setContentTypeModalOpen(true);
  }

  function handleChooseCustom() {
    setContentTypeModalOpen(false);
    document.getElementById('input_file').click();
  }

  function handleChooseLibrary() {
    setContentTypeModalOpen(false);
    setLibraryPickerOpen(true);
  }

  function handleChoosePlugin() {
    setContentTypeModalOpen(false);
    getPlugins(token)
      .then((res) => setAvailablePlugins(res.data.plugins || []))
      .catch(() => setAvailablePlugins([]));
    setPluginPickerOpen(true);
  }

  function handleSelectPlugin(pluginId) {
    setPluginPickerOpen(false);
    if (pluginId === 'weather') setWeatherConfigOpen(true);
    else if (pluginId === 'sports') setSportsConfigOpen(true);
    else if (pluginId === 'social') setSocialConfigOpen(true);
    else if (pluginId === 'trivia') setTriviaConfigOpen(true);
  }

  async function handleSavePlugin(pluginId, config) {
    if (contentToReplace != null && isSlotRendering(Number(contentToReplace))) {
      return;
    }
    setPluginSaving(true);
    try {
      await startPluginJob({
        userId: ownerUserId,
        slotId: Number(contentToReplace),
        pluginId,
        config,
      });
      setPluginRenderingSlot(Number(contentToReplace));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e?.message || 'Failed to start plugin render. Please try again.');
    } finally {
      setPluginSaving(false);
    }
  }

  function closePluginConfigModal() {
    setWeatherConfigOpen(false);
    setSportsConfigOpen(false);
    setSocialConfigOpen(false);
    setTriviaConfigOpen(false);
    const slotId = contentToReplace;
    setContentToReplace(null);
    if (slotId == null || !isSlotRendering(slotId)) {
      setPluginRenderingSlot(null);
    }
    if (slotId != null) {
      pluginReviewFollowUpRef.current = slotId;
      loadScreenCore().catch(() => undefined);
    }
  }

  async function handleClearSlot(slotPosition) {
    if (!window.confirm('Remove content from this slot?')) return;
    setLoading(true);
    try {
      cancelPendingJobForSlot(slotPosition);
      if (pluginRenderingSlot === slotPosition) {
        setPluginRenderingSlot(null);
      }
      setWeatherConfigOpen(false);
      setSportsConfigOpen(false);
      setSocialConfigOpen(false);
      if (contentToReplace === slotPosition) {
        setContentToReplace(null);
      }
      await clearContentSlot({
        userId: ownerUserId,
        screenId: activeScreenId,
        slotId: slotPosition,
      }, token);
      await updateScreen();
    } catch (e) {
      console.error(e);
      alert('Failed to clear slot.');
    } finally {
      setLoading(false);
    }
  }

  function getActiveSlotConfig() {
    const pending = contentToReplace ? getSlotPending(contentToReplace) : null;
    if (pending?.config) {
      return pending.config;
    }
    const slot = personalContentSlots.find((s) => s.slotPosition === contentToReplace);
    return slot?.pluginConfig || null;
  }

  function getActiveSlotContentUrl() {
    const slot = personalContentSlots.find((s) => s.slotPosition === contentToReplace);
    if (!slot) return null;
    if (slot.reviewStatus === 'pending_approval' && slot.pendingContentUrl) {
      return slot.pendingContentUrl;
    }
    return slot.contentUrl || null;
  }

  function isActivePluginModalRendering() {
    const slotId = contentToReplace ?? pluginRenderingSlot;
    if (slotId == null) return false;
    return pluginRenderingSlot === slotId || isSlotRendering(slotId);
  }

  async function fileUploadOnChange(e) {
    const input = document.querySelector('#' + e.target.id);
    const files = input?.files;
    if (!files || !files[0]) {
      return;
    }

    let file = files[0];
    const ext = fileExtensionLower(file.name);
    const mime = (file.type || '').toLowerCase();
    const videoMime = mime.startsWith('video/');
    const videoByExt = ['mp4', 'mov', 'webm'].includes(ext);
    const isVideo = videoMime || videoByExt;

    if (isVideo) {
      if (file.size > PERSONAL_VIDEO_MAX_BYTES) {
        alert('This video is too large. Maximum upload size is 80MB. Try a shorter clip or lower resolution.');
        input.value = '';
        return;
      }
      if (file.size > 12 * 1024 * 1024) {
        alert('Large videos are automatically re-encoded to reduce size and fit signage playback.');
      }
    } else {
      if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
        alert('Please upload JPG, PNG, WebP, MP4, MOV, or WebM');
        input.value = '';
        return;
      }
      if (file.size > PERSONAL_IMAGE_MAX_BYTES) {
        setLoading(true);
        try {
          ({ file } = await prepareImageFileForUpload(file, PERSONAL_IMAGE_MAX_BYTES));
        } catch (err) {
          console.error(err);
          alert(err?.message || 'Could not resize image. Please try a smaller file.');
          input.value = '';
          setLoading(false);
          return;
        }
      }
    }

    const uploadExt = fileExtensionLower(file.name) || ext;
    try {
      const b64 = await readFileAsBase64(file);
      await updateContent(b64, {
        contentKind: isVideo ? 'video' : 'image',
        mimeType: file.type || undefined,
        originalExtension: uploadExt || undefined,
      });
    } catch (err) {
      console.error(err);
      alert('Could not read file. Please try again.');
      input.value = '';
      setLoading(false);
    }
  }
  async function updateContent(fileData, mediaMeta) {
    setLoading(true);
    const contentInformation = {
      content: fileData,
      userId: ownerUserId,
      slotId: Number(contentToReplace),
      screenId: activeScreenId,
      contentKind: mediaMeta.contentKind,
      mimeType: mediaMeta.mimeType,
      originalExtension: mediaMeta.originalExtension,
    };
    try {
      const submittedSlot = Number(contentToReplace);
      await updateUserContentSlot(contentInformation, token);
      await updateScreen();
      setContentToReplace(null);
      setContentReviewModal({ slotPosition: submittedSlot, mode: 'pending' });
      const input = document.getElementById('input_file');
      if (input) {
        input.value = '';
      }
    } catch (e) {
      console.error(e);
      const apiMessage = e?.response?.data?.error;
      alert(
        typeof apiMessage === 'string' && apiMessage
          ? apiMessage
          : 'Upload failed. Please try again with a smaller file or a different format (JPG, PNG, WebP, MP4, MOV, or WebM).',
      );
    } finally {
      setLoading(false);
    }
  }
  async function toggleScreen() {
    setLoading(true);
    try {
      await toggleScreenActive({setting: !screenOn, screenId: activeScreenId}, token);
      await updateScreen();
      setAlertActive(false);
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false);
    }
  }

  async function saveDisplayOrientation(next) {
    if (!ownerUserId || !activeScreenId) {
      alert('Screen session expired. Return to Your Screen Stats and open the screen again.');
      return;
    }
    const normalized = next === 'portrait' ? 'portrait' : 'landscape';
    setLoading(true);
    try {
      await updateScreenDisplayOrientation(token, {
        screenId: state.screenId,
        displayOrientation: normalized,
      });
      await updateScreen();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Could not update display orientation.');
    } finally {
      setLoading(false);
    }
  }

  async function saveContentRotation(next) {
    if (!ownerUserId || !activeScreenId) {
      alert('Screen session expired. Return to Your Screen Stats and open the screen again.');
      return;
    }
    const normalized = next === 'left' || next === 'right' ? next : 'none';
    setLoading(true);
    try {
      await updateScreenDisplayOrientation(token, {
        screenId: state.screenId,
        contentRotation: normalized,
      });
      await updateScreen();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Could not update content rotation.');
    } finally {
      setLoading(false);
    }
  }

  async function saveSchedule() {
    setScheduleSaveError('');
    if (scheduleEnabledUi && scheduleWindows.some((w) => !Array.isArray(w.dows) || !w.dows.length)) {
      setScheduleSaveError('Each time window must include at least one day.');
      return;
    }
    setLoading(true);
    try {
      const tz =
        scheduleTimezone ||
        (typeof Intl !== 'undefined' && Intl.DateTimeFormat
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : 'UTC');
      await updateScreenSchedule(
        {
          userId: ownerUserId,
          screenId: activeScreenId,
          scheduleEnabled: scheduleEnabledUi,
          scheduleTimezone: tz,
          scheduleRules: scheduleWindowsToRules(scheduleWindows),
        },
        token,
      );
      await updateScreen();
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        (Array.isArray(e?.response?.data?.errors) && e.response.data.errors.join(' ')) ||
        'Could not save schedule.';
      setScheduleSaveError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function patchScheduleWindow(index, patch) {
    setScheduleWindows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  function addScheduleWindow() {
    setScheduleWindows((prev) => {
      const last = prev[prev.length - 1];
      return [
        ...prev,
        {
          dows: [firstUnusedDow(prev)],
          start: last?.start ?? '09:00',
          end: last?.end ?? '17:00',
        },
      ];
    });
  }

  function removeScheduleWindow(index) {
    setScheduleWindows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  function applyCoreScreenData(result, cacheKey = Date.now()) {
    const screenRow = result.data.screensData?.[0] || {};
    setStats((prev) => ({
      ...prev,
      ...screenRow,
    }));
    setScreenNameDraft(screenRow.ScreenName || '');
    setScreenNameError('');
    setScreenOn(result.data.turnedOn.TurnedOn);
    setDisplayOrientation(result.data.displayOrientation === 'portrait' ? 'portrait' : 'landscape');
    const cr = (result.data.contentRotation || 'none').toLowerCase();
    setContentRotation(cr === 'left' || cr === 'right' ? cr : 'none');
    setPlaybackActive(Boolean(result.data.playbackActive));
    const sch = result.data.schedule || {};
    setScheduleEnabledUi(Boolean(sch.enabled));
    const defaultTz =
      sch.timezone ||
      (typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'UTC');
    setScheduleTimezone(defaultTz);
    if (Array.isArray(sch.rules) && sch.rules.length) {
      setScheduleWindows(scheduleRulesToWindows(sch.rules));
    } else {
      setScheduleWindows([{ ...DEFAULT_SCHEDULE_WINDOW }]);
    }
    setPersonalContentSlots((result.data.personalContentSlots || []).map(slot => ({
      ...slot,
      contentUrl: appendCacheBuster(slot.contentUrl, cacheKey),
      pendingContentUrl: appendCacheBuster(slot.pendingContentUrl, cacheKey),
    })));
    setAdvertisingSlots((result.data.advertisingSlots || []).map(slot => ({
      ...slot,
      adContentHorizontal: appendCacheBuster(slot.adContentHorizontal, cacheKey),
      adContentVertical: appendCacheBuster(slot.adContentVertical, cacheKey),
    })));
  }

  function loadScreenCore() {
    setCoreLoading(true);
    return getUserDashboardScreenInfo(token, ownerUserId, activeScreenId)
      .then((result) => {
        applyCoreScreenData(result);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => setCoreLoading(false));
  }

  function loadScreenRevenue() {
    if (!canViewRevenue) {
      setRevenueLoading(false);
      return Promise.resolve();
    }
    setRevenueLoading(true);
    return getUserScreenRevenue(token, ownerUserId, activeScreenId)
      .then((result) => {
        setStats((prev) => ({
          ...prev,
          grossRevenue: result.data.grossRevenue,
          hostEarnings: result.data.hostEarnings,
          hostPaid: result.data.hostPaid,
          balanceOwed: result.data.balanceOwed,
          revenueSharePercent: result.data.revenueSharePercent,
          TotalCostSum: result.data.TotalCostSum,
          TotalCostSumPaid: result.data.TotalCostSumPaid,
        }));
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => setRevenueLoading(false));
  }

  function loadScreenActivity() {
    setActivityLoading(true);
    return getUserScreenActivity(token, ownerUserId, activeScreenId)
      .then((result) => {
        setHoursActive(result.data.totalMinutesActive ?? 0);
        setScreenDataForGraph(result.data.screenDataForGraph || {});
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => setActivityLoading(false));
  }

  function updateScreen() {
    return Promise.all([loadScreenCore(), loadScreenRevenue(), loadScreenActivity()]);
  }

  function refreshScreenAfterAction() {
    setLoading(true);
    return updateScreen().finally(() => setLoading(false));
  }

  async function saveScreenName() {
    if (!token || !ownerUserId || !activeScreenId) return;
    setScreenNameSaving(true);
    setScreenNameError('');
    try {
      const res = await updateScreenName(token, {
        userId: ownerUserId,
        screenId: activeScreenId,
        screenName: screenNameDraft.trim(),
      });
      const savedName = res.data?.screenName || null;
      setStats((prev) => ({ ...prev, ScreenName: savedName }));
      setScreenNameDraft(savedName || '');
    } catch (err) {
      setScreenNameError(err?.response?.data?.error || 'Could not save screen name');
    } finally {
      setScreenNameSaving(false);
    }
  }

  function updateBlacklistedCategories(newCategories){
    setLoading(true);
    setSelectedBlacklistCategories(newCategories);
    updateBlacklistedAdCategories(token, {categoryIds: newCategories.map(cat => cat.value), userId: ownerUserId}).then(response => {
      console.log("Blacklisted categories updated");
    }).catch(e => {
      console.error(e);
    }).finally(() => setLoading(false));
  }
  useEffect(()=>{
    if (location.state) {
      sessionStorage.setItem('userDashboardScreenInformationState', JSON.stringify(location.state));
      persistUserSession(location.state);
    }
  }, [location.state]);

  useEffect(() => {
    if (activeScreenId) {
      persistUserSession({ screenId: activeScreenId });
    }
  }, [activeScreenId]);

  useEffect(()=>{
    if (!token || !ownerUserId || !activeScreenId) {
      navigate("/");
      return;
    }

    updateScreen();
    getAllCategories();

  }, [navigate, activeScreenId, ownerUserId, token]);

  useEffect(() => {
    if (!activeScreenId || coreLoading) return;

    const followUpSlot = pluginReviewFollowUpRef.current;
    if (followUpSlot != null) {
      const slot = personalContentSlots.find((s) => s.slotPosition === followUpSlot);
      if (slot?.reviewStatus === 'pending_approval' && slot?.pendingContentUrl) {
        setContentReviewModal({ slotPosition: followUpSlot, mode: 'pending' });
        pluginReviewFollowUpRef.current = null;
        return;
      }
      pluginReviewFollowUpRef.current = null;
    }

    if (contentReviewModal) return;

    const outcomeSlot = personalContentSlots.find((slot) => (
      slot.lastReviewStatus
      && slot.lastReviewAt
      && !isContentReviewSeen(activeScreenId, slot.slotPosition, slot.lastReviewAt)
    ));
    if (outcomeSlot) {
      setContentReviewModal({
        slotPosition: outcomeSlot.slotPosition,
        mode: outcomeSlot.lastReviewStatus,
      });
    }
  }, [personalContentSlots, activeScreenId, coreLoading, contentReviewModal]);

  function closeContentReviewModal() {
    if (contentReviewModal) {
      const slot = personalContentSlots.find((s) => s.slotPosition === contentReviewModal.slotPosition);
      if (
        slot
        && (contentReviewModal.mode === 'approved' || contentReviewModal.mode === 'rejected')
        && slot.lastReviewAt
      ) {
        markContentReviewSeen(activeScreenId, slot.slotPosition, slot.lastReviewAt);
      }
    }
    setContentReviewModal(null);
  }

  function handleContentReviewReplace() {
    const slotPosition = contentReviewModal?.slotPosition;
    closeContentReviewModal();
    if (slotPosition != null) {
      setContentToReplace(slotPosition);
      setContentTypeModalOpen(true);
    }
  }

  async function handleContentReviewCancelSubmission() {
    const slotPosition = contentReviewModal?.slotPosition;
    closeContentReviewModal();
    if (slotPosition != null) {
      await handleClearSlot(slotPosition);
    }
  }

  const activeReviewSlot = contentReviewModal
    ? personalContentSlots.find((s) => s.slotPosition === contentReviewModal.slotPosition)
    : null;

  function renderStatValue(loadingFlag, value) {
    if (loadingFlag) {
      return <SkeletonText width="72px" height="1.1em" />;
    }
    return value;
  }

  return (
    <div style={{padding: '20px', maxWidth: '1400px', margin: '0 auto'}}>
      {loading && (
        <div className='uds-loading-overlay'>
          <RingLoader
            color={'#C52F00'}
            loading={true}
            size={60}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      )}

      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '20px'}}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span className='userDashboardTitle' style={{margin: 0}}>
              {stats.ScreenName || `Screen #${activeScreenId}`}
            </span>
            {stats.ScreenName ? (
              <span style={{ fontFamily: 'Inter-Medium', fontSize: '14px', color: '#6b7280' }}>
                #{activeScreenId}
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {coreLoading ? (
              <>
                <SkeletonText width="220px" height="37px" />
                <SkeletonText width="96px" height="37px" />
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={screenNameDraft}
                  onChange={(e) => setScreenNameDraft(e.target.value)}
                  placeholder="Screen name (optional)"
                  maxLength={100}
                  disabled={screenNameSaving || !canManageSettings}
                  style={{
                    minWidth: '220px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontFamily: 'Inter-Medium',
                    fontSize: '14px',
                  }}
                  aria-label="Screen name"
                />
                <button
                  type="button"
                  onClick={saveScreenName}
                  disabled={screenNameSaving || !canManageSettings}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#C52F00',
                    color: '#fff',
                    fontFamily: 'Inter-Medium',
                    fontSize: '14px',
                    cursor: screenNameSaving ? 'wait' : 'pointer',
                    opacity: screenNameSaving ? 0.7 : 1,
                  }}
                >
                  {screenNameSaving ? 'Saving…' : 'Save name'}
                </button>
              </>
            )}
          </div>
          {screenNameError ? (
            <span style={{ color: '#b91c1c', fontSize: '13px', fontFamily: 'Inter-Medium' }}>
              {screenNameError}
            </span>
          ) : null}
        </div>
        {canManageSettings ? (
        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginRight: '20px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            <span style={{fontFamily: 'Inter-Medium', fontSize: '14px', color: '#374151'}}>Display</span>
            <button
              type="button"
              onClick={() => displayOrientation !== 'landscape' && saveDisplayOrientation('landscape')}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: displayOrientation === 'landscape' ? '2px solid #C52F00' : '1px solid #d1d5db',
                background: displayOrientation === 'landscape' ? '#fff5f2' : '#fff',
                cursor: displayOrientation === 'landscape' ? 'default' : 'pointer',
                fontFamily: 'Inter-Medium',
                fontSize: '14px',
              }}
            >
              Landscape
            </button>
            <button
              type="button"
              onClick={() => displayOrientation !== 'portrait' && saveDisplayOrientation('portrait')}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: displayOrientation === 'portrait' ? '2px solid #C52F00' : '1px solid #d1d5db',
                background: displayOrientation === 'portrait' ? '#fff5f2' : '#fff',
                cursor: displayOrientation === 'portrait' ? 'default' : 'pointer',
                fontFamily: 'Inter-Medium',
                fontSize: '14px',
              }}
            >
              Portrait
            </button>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
            <span style={{fontFamily: 'Inter-Medium', fontSize: '14px', color: '#374151'}}>Rotate content</span>
            <button
              type="button"
              onClick={() => contentRotation !== 'none' && saveContentRotation('none')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: contentRotation === 'none' ? '2px solid #C52F00' : '1px solid #d1d5db',
                background: contentRotation === 'none' ? '#fff5f2' : '#fff',
                cursor: contentRotation === 'none' ? 'default' : 'pointer',
                fontFamily: 'Inter-Medium',
                fontSize: '13px',
              }}
            >
              None
            </button>
            <button
              type="button"
              onClick={() => contentRotation !== 'left' && saveContentRotation('left')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: contentRotation === 'left' ? '2px solid #C52F00' : '1px solid #d1d5db',
                background: contentRotation === 'left' ? '#fff5f2' : '#fff',
                cursor: contentRotation === 'left' ? 'default' : 'pointer',
                fontFamily: 'Inter-Medium',
                fontSize: '13px',
              }}
              title="Rotate images and video 90° counter-clockwise"
            >
              Left
            </button>
            <button
              type="button"
              onClick={() => contentRotation !== 'right' && saveContentRotation('right')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: contentRotation === 'right' ? '2px solid #C52F00' : '1px solid #d1d5db',
                background: contentRotation === 'right' ? '#fff5f2' : '#fff',
                cursor: contentRotation === 'right' ? 'default' : 'pointer',
                fontFamily: 'Inter-Medium',
                fontSize: '13px',
              }}
              title="Rotate images and video 90° clockwise"
            >
              Right
            </button>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <span style={{fontFamily: 'Inter-Medium', fontSize: '16px', color: screenOn ? '#10b981' : '#6b7280'}}>
              {screenOn ? 'Active' : 'Inactive'}
            </span>
            <ToggleButton 
              className="toggleButton"
              value={screenOn || false}
              onToggle={(value) => {
                setAlertActive(true);
              }} 
            />
          </div>
        </div>
        ) : null}
      </div>
      {canManageSettings && alertActive &&<ReactjsAlert 
        status={alertActive} // true or false
        type={"warning"} // success, warning, error, info
        title={"Screen Power"}
        quotes={true}
        quote={`Are you sure you want to turn ${screenOn && "off" || "on"} your screen?`}
        Close={() => toggleScreen(!screenOn)}
        color="#1E1E1E"

      />}
      {canManageSettings ? (
      <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '12px', background: '#fafafa' }}>
        <span className="userDashboardScreenInfoActivityGraphTitle" style={{ display: 'block', marginBottom: '8px' }}>Operating hours</span>
        <p style={{ fontFamily: 'Inter-Medium', fontSize: '14px', color: '#4b5563', marginBottom: '12px', lineHeight: 1.5 }}>
          Playback runs only when Power is on and—if you enable a schedule—the current time is inside an active window (in the timezone you choose).
          If end time is at or before start time on the same row, that window crosses midnight into the next day.
        </p>
        <p style={{ fontFamily: 'Inter-Medium', fontSize: '14px', color: '#111827', marginBottom: '16px' }}>
          Effective playback now:{' '}
          <span style={{ color: playbackActive ? '#10b981' : '#6b7280' }}>{playbackActive ? 'On' : 'Off'}</span>
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Inter-Medium', fontSize: '14px', color: '#374151' }}>Use schedule</span>
          <ToggleButton
            className="toggleButton"
            value={scheduleEnabledUi || false}
            onToggle={() => setScheduleEnabledUi(!scheduleEnabledUi)}
          />
        </div>
        <div style={{ marginBottom: '12px', maxWidth: '420px' }}>
          <span style={{ fontFamily: 'Inter-Medium', fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>Timezone</span>
          <Select
            className="multiSelectNewCampaignInput"
            isDisabled={!scheduleEnabledUi}
            value={TIMEZONE_SELECT_OPTIONS.find((o) => o.value === scheduleTimezone) || null}
            onChange={(opt) => setScheduleTimezone(opt ? opt.value : '')}
            options={TIMEZONE_SELECT_OPTIONS}
            placeholder="Select timezone"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
          {scheduleWindows.map((row, idx) => (
            <div
              key={`sw-${idx}`}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
                opacity: scheduleEnabledUi ? 1 : 0.55,
              }}
            >
              <div style={{ minWidth: '220px', flex: '1 1 220px' }}>
                <span style={{ fontFamily: 'Inter-Medium', fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                  Days
                </span>
                <Select
                  className="multiSelectNewCampaignInput"
                  isMulti
                  closeMenuOnSelect={false}
                  isDisabled={!scheduleEnabledUi}
                  value={ISO_WEEKDAY_OPTIONS.filter((d) => (row.dows || []).includes(d.value))}
                  onChange={(opts) =>
                    patchScheduleWindow(idx, {
                      dows: (opts || []).map((o) => o.value).sort((a, b) => a - b),
                    })
                  }
                  options={ISO_WEEKDAY_OPTIONS}
                  placeholder="Select days"
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151' }}>
                Start
                <input
                  type="time"
                  disabled={!scheduleEnabledUi}
                  value={row.start}
                  onChange={(e) => patchScheduleWindow(idx, { start: e.target.value })}
                  style={{ padding: '6px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151' }}>
                End
                <input
                  type="time"
                  disabled={!scheduleEnabledUi}
                  value={row.end}
                  onChange={(e) => patchScheduleWindow(idx, { end: e.target.value })}
                  style={{ padding: '6px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </label>
              <button
                type="button"
                disabled={!scheduleEnabledUi || scheduleWindows.length <= 1}
                onClick={() => removeScheduleWindow(idx)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: scheduleEnabledUi && scheduleWindows.length > 1 ? 'pointer' : 'not-allowed',
                  fontFamily: 'Inter-Medium',
                  fontSize: '13px',
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
          <button
            type="button"
            disabled={!scheduleEnabledUi}
            onClick={addScheduleWindow}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #C52F00',
              background: scheduleEnabledUi ? '#fff5f2' : '#f3f4f6',
              color: '#C52F00',
              cursor: scheduleEnabledUi ? 'pointer' : 'not-allowed',
              fontFamily: 'Inter-Medium',
              fontSize: '14px',
            }}
          >
            Add window
          </button>
          <button
            type="button"
            onClick={saveSchedule}
            style={{
              padding: '8px 18px',
              borderRadius: '8px',
              border: 'none',
              background: '#C52F00',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'Inter-Medium',
              fontSize: '14px',
            }}
          >
            Save schedule
          </button>
        </div>
        {scheduleSaveError ? (
          <p style={{ color: '#b91c1c', fontSize: '14px', marginTop: '8px' }}>{scheduleSaveError}</p>
        ) : null}
      </div>
      ) : null}
      <div className='userDashboardScreenInfoQuickStats'>
        {canViewRevenue ? (
        <>
        <div className='userDashboardScreenInfoQuickStatsTotalRev'>
          <span className='userDashboardScreenInfoQuickStatsTitle'>Your Earnings</span>
          <span className='userDashboardScreenInfoQuickStatsContent'>
            {renderStatValue(revenueLoading, `$${stats.hostEarnings ?? stats.TotalCostSum ?? 0}`)}
          </span>
        </div>
        <div className='userDashboardScreenInfoQuickStatsTotalPaid'>
          <span className='userDashboardScreenInfoQuickStatsTitle'>Paid</span>
          <span className='userDashboardScreenInfoQuickStatsContent'>
            {renderStatValue(revenueLoading, `$${stats.hostPaid ?? stats.TotalCostSumPaid ?? 0}`)}
          </span>
        </div>
        <div className='userDashboardScreenInfoQuickStatsTotalPaid'>
          <span className='userDashboardScreenInfoQuickStatsTitle'>Balance Owed</span>
          <span className='userDashboardScreenInfoQuickStatsContent'>
            {renderStatValue(revenueLoading, `$${stats.balanceOwed || 0}`)}
          </span>
        </div>
        <div className='userDashboardScreenInfoQuickStatsTotalPaid'>
          <span className='userDashboardScreenInfoQuickStatsTitle'>Gross Revenue</span>
          <span className='userDashboardScreenInfoQuickStatsContent'>
            {renderStatValue(revenueLoading, `$${stats.grossRevenue || 0}`)}
          </span>
        </div>
        </>
        ) : null}
        <div className='userDashboardScreenInfoQuickStatsTotalHrsActive'>
          <span className='userDashboardScreenInfoQuickStatsTitle'>Total Hours Active</span>
          <span className='userDashboardScreenInfoQuickStatsContent'>
            {renderStatValue(activityLoading, ((parseFloat(hoursActive)) / 60).toFixed(2))}
          </span>

        </div>
        <div className='userDashboardScreenInfoQuickStatsTotalLastActive'>
          <span className='userDashboardScreenInfoQuickStatsTitle'>Last Active</span>
          <span className='userDashboardScreenInfoQuickStatsContent'>
            {renderStatValue(coreLoading, formatLastActive(stats.LastActive))}
          </span>

        </div>
      </div>
      <span className='userDashboardScreenInfoActivityGraphTitle'>Personal Content (6 Slots)</span>
      <span className='userDashboardScreenInfoSubtitle'>Upload custom slides or add live plugins (weather, sports)</span>

      <div className='userDashboardScreenInfoContent'>
        <span className='bestSizeMessage'>Best size: 1920×1080. Images up to 3MB (large images are resized automatically); videos up to 80MB (large videos are re-encoded).</span>
        <input
          type="file"
          name=""
          id="input_file"
          hidden
          accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
          onChange={(e) => {
            fileUploadOnChange(e);
          }}
        />
        {[7, 8, 9, 10, 11, 12].map((slotPosition) => {
          if (coreLoading) {
            return (
              <div
                key={slotPosition}
                className="userDashboardScreenInfoContentImage userDashboardScreenInfoContentSkeleton"
                aria-busy="true"
              >
                <SkeletonText width="70%" height="1em" />
              </div>
            );
          }

          const slot = personalContentSlots.find(s => s.slotPosition === slotPosition);
          const isLastSlot = slotPosition === 10;
          const displaySlotPosition = slotPosition - 6;
          const isPlugin = slot?.contentType === 'plugin';
          const hasPendingReview = slot?.reviewStatus === 'pending_approval' && slot?.pendingContentUrl;
          const hasLive = Boolean(slot?.contentUrl);
          const isRendering = isSlotRendering(slotPosition);

          if (hasLive || hasPendingReview) {
            const previewUrl = hasPendingReview ? slot.pendingContentUrl : slot.contentUrl;
            const openSlot = () => openSlotEditor(slotPosition);
            const mediaEl = isPersonalContentVideoUrl(previewUrl) ? (
              <video
                key={slotPosition}
                onClick={openSlot}
                className={`userDashboardScreenInfoContentImage ${isLastSlot ? 'userDashboardScreenInfoContentImageLast' : ''}`}
                src={previewUrl}
                muted
                playsInline
                loop
                controls
              />
            ) : (
              <img
                key={slotPosition}
                onClick={openSlot}
                className={`userDashboardScreenInfoContentImage ${isLastSlot ? 'userDashboardScreenInfoContentImageLast' : ''}`}
                src={previewUrl}
                alt=""
              />
            );
            return (
              <div
                key={slotPosition}
                className={`userDashboardScreenInfoContentSlotWrap ${hasPendingReview ? 'hasPendingReview' : ''}`}
              >
                {hasPendingReview && (
                  <span className="contentPluginSlotRenderingBadge pendingReview">Tap for review status</span>
                )}
                {isPlugin && hasLive && !hasPendingReview && <PluginSlotBadge pluginId={slot.pluginId} />}
                {isRendering ? (
                  <span className="contentPluginSlotRenderingBadge">Generating image…</span>
                ) : null}
                {mediaEl}
                {hasPendingReview ? (
                  <span className="userDashboardScreenInfoPendingTapHint">View submission status</span>
                ) : null}
                {!hasPendingReview ? (
                  <div className="contentPluginSlotActions">
                    <button
                      type="button"
                      className="contentPluginSlotActionBtn"
                      onClick={(e) => { e.stopPropagation(); openSlotEditor(slotPosition); }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="contentPluginSlotActionBtn"
                      onClick={(e) => { e.stopPropagation(); handleClearSlot(slotPosition); }}
                    >
                      Clear
                    </button>
                  </div>
                ) : null}
              </div>
            );
          }

          return (
            <div
              key={slotPosition}
              onClick={() => openSlotEditor(slotPosition)}
              className='userDashboardScreenInfoContentEmptyContent'
              style={{ position: 'relative' }}
            >
              {isRendering ? (
                <span className="contentPluginSlotRenderingBadge">Generating image…</span>
              ) : null}
              <div className='userDashboardScreenInfoContentEmptyContentPlusText'>
                <span className='userDashboardScreenInfoContentEmptyContentPlus'>+</span>
                <span className='userDashboardScreenInfoContentEmptyContentText'>Slot {displaySlotPosition}: Custom or Plugin</span>
              </div>
            </div>
          );
        })}
      </div>
      <span className='userDashboardScreenInfoActivityGraphTitle'>Advertising Campaigns</span>
      <span className='userDashboardScreenInfoSubtitle'>Active and pending campaigns currently shown on this screen</span>
      
      <div className='userDashboardAdvertisingSlots'>
        {coreLoading ? (
          [1, 2, 3].map((placeholder) => (
            <div key={placeholder} className='advertisingSlotWrapper' aria-busy="true">
              <div className='advertisingSlotCard available'>
                <div className='slotCardDetails' style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <SkeletonText width="55%" height="1em" />
                  <SkeletonText width="75%" height="1em" />
                  <SkeletonText width="45%" height="1em" />
                </div>
              </div>
            </div>
          ))
        ) : (
          <>
        {advertisingSlots.map((slot, index) => {
          const rank = slot.slotPosition || (index + 1);
          const slotKey = slot.advertisementId || slot.AdvertisementId || `${rank}-${slot.companyName || 'campaign'}`;
          const isOccupied = slot?.occupied && slot.subscriptionStatus === 'active';
          const isPending = slot?.occupied && (slot.subscriptionStatus === 'pending' || slot.subscriptionStatus === 'pending_approval');
          const isExpanded = expandedSlot === slotKey;
          
          let statusClass = 'available';
          let statusText = '○ Available';
          let statusIcon = '○';
          
          if (isOccupied) {
            statusClass = 'occupied';
            statusText = '✓ Occupied';
            statusIcon = '✓';
          } else if (isPending) {
            statusClass = 'pending';
            statusText = '⏳ Pending Approval';
            statusIcon = '⏳';
          }
          
          return (
            <div key={slotKey} className='advertisingSlotWrapper'>
              <div 
                className={`advertisingSlotCard ${statusClass} ${isOccupied ? 'clickable' : ''} ${isExpanded ? 'expanded' : ''}`}
                onClick={() => {
                  if (isOccupied) {
                    setExpandedSlot(isExpanded ? null : slotKey);
                  }
                }}
              >
                <div className='slotCardHeader'>
                  <span className='slotCardNumber'>Campaign #{rank}</span>
                  <span className={`slotCardStatus ${statusClass}`}>
                    {statusText}
                  </span>
                </div>
                {isOccupied ? (
                  <div className='slotCardDetails'>
                    <span className='slotCardLabel'><strong>Advertiser:</strong> {slot.companyName}</span>
                    <span className='slotCardLabel'><strong>Campaign:</strong> {slot.campaignName}</span>
                    <span className='slotCardLabel'><strong>Monthly Fee:</strong> ${(slot.monthlyFee || 500).toFixed(2)}</span>
                    <span className='slotCardLabel'><strong>Status:</strong> {slot.subscriptionStatus}</span>
                  </div>
                ) : isPending ? (
                  <div className='slotCardDetails'>
                    <span className='slotCardLabel'><strong>Advertiser:</strong> {slot.companyName}</span>
                    <span className='slotCardLabel'><strong>Campaign:</strong> {slot.campaignName}</span>
                    <span className='slotCardLabel'><strong>Monthly Fee:</strong> ${(slot.monthlyFee || 500).toFixed(2)}</span>
                    <span className='slotCardLabel pending'><strong>Status:</strong> Awaiting admin approval</span>
                  </div>
                ) : (
                  <div className='slotCardDetails'>
                    <span className='slotCardLabel emptySlot'>No active creative available</span>
                  </div>
                )}
              </div>
              {isOccupied && isExpanded && (slot.adContentHorizontal || slot.adContentVertical) && (
                <div className='adContentPreview'>
                  {slot.adContentHorizontal && (
                    <div className='adContentItem'>
                      <span className='adContentLabel'>Horizontal</span>
                      <img src={slot.adContentHorizontal} alt='Horizontal Ad' className='adContentImage' />
                    </div>
                  )}
                  {slot.adContentVertical && (
                    <div className='adContentItem'>
                      <span className='adContentLabel'>Vertical</span>
                      <img src={slot.adContentVertical} alt='Vertical Ad' className='adContentImage' />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {advertisingSlots.length === 0 && (
          <div className='advertisingSlotWrapper'>
            <div className='advertisingSlotCard available'>
              <div className='slotCardDetails'>
                <span className='slotCardLabel emptySlot'>No advertising campaigns found for this screen.</span>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
      {canManageSettings ? (
      <>
      <span className='userDashboardScreenInfoActivityGraphTitle'>Blacklist</span>
 <div className='advertiserBlacklistContainer'>
                            <span className='newCampaignInputTitle'>Advertisement Type(s)</span>
                            {categoriesLoading ? (
                              <SkeletonText width="100%" height="42px" />
                            ) : (
                              <Select value={selectedBlacklistCategories} onChange={updateBlacklistedCategories} className="multiSelectNewCampaignInput" required isMulti options={allCategories} />
                            )}
                            </div>
      </>
      ) : null}
      <span className='userDashboardScreenInfoActivityGraphTitle'>Analytics</span>

      <div className='userDashboardScreenInfoActivityGraph'>

      </div>
      <ContentTypeModal
        open={contentTypeModalOpen}
        onClose={() => { setContentTypeModalOpen(false); setContentToReplace(null); }}
        onChooseCustom={handleChooseCustom}
        onChooseLibrary={handleChooseLibrary}
        onChoosePlugin={handleChoosePlugin}
      />
      <LibraryPickerModal
        open={libraryPickerOpen && contentToReplace != null}
        token={token}
        ownerUserId={ownerUserId}
        screenId={activeScreenId}
        slotId={Number(contentToReplace)}
        onClose={() => {
          setLibraryPickerOpen(false);
          setContentToReplace(null);
        }}
        onPublished={() => {
          refreshScreenAfterAction().catch(() => undefined);
        }}
      />
      <PluginPickerModal
        open={pluginPickerOpen}
        plugins={availablePlugins}
        onClose={() => setPluginPickerOpen(false)}
        onSelectPlugin={handleSelectPlugin}
      />
      <WeatherPluginConfig
        open={weatherConfigOpen}
        onClose={closePluginConfigModal}
        onSave={(config) => handleSavePlugin('weather', config)}
        userId={ownerUserId}
        screenId={activeScreenId}
        token={token}
        initialConfig={getActiveSlotConfig()}
        saving={pluginSaving}
        rendering={isActivePluginModalRendering()}
      />
      <SportsPluginConfig
        open={sportsConfigOpen}
        onClose={closePluginConfigModal}
        onSave={(config) => handleSavePlugin('sports', config)}
        userId={ownerUserId}
        screenId={activeScreenId}
        token={token}
        initialConfig={getActiveSlotConfig()}
        saving={pluginSaving}
        rendering={isActivePluginModalRendering()}
      />
      <SocialPluginConfig
        open={socialConfigOpen}
        onClose={closePluginConfigModal}
        onSave={(config) => handleSavePlugin('social', config)}
        userId={ownerUserId}
        token={token}
        initialConfig={getActiveSlotConfig()}
        saving={pluginSaving}
        rendering={isActivePluginModalRendering()}
      />
      <TriviaPluginConfig
        open={triviaConfigOpen}
        onClose={closePluginConfigModal}
        onSave={(config) => handleSavePlugin('trivia', config)}
        saving={pluginSaving}
        rendering={isActivePluginModalRendering()}
        contentUrl={getActiveSlotContentUrl()}
        token={token}
        orientation={displayOrientation}
        initialConfig={getActiveSlotConfig()}
      />
      <ContentReviewStatusModal
        open={Boolean(contentReviewModal && activeReviewSlot)}
        mode={contentReviewModal?.mode || 'pending'}
        slot={activeReviewSlot}
        displaySlotNumber={
          activeReviewSlot?.slotPosition != null
            ? activeReviewSlot.slotPosition - 6
            : null
        }
        onClose={closeContentReviewModal}
        onEdit={contentReviewModal?.mode === 'pending' ? handleContentReviewReplace : undefined}
        onClear={contentReviewModal?.mode === 'pending' ? handleContentReviewCancelSubmission : undefined}
      />
    </div>
  )
}
